import React from "react";
import { BaseAction, Fetcher, Query } from "@al/aku-lc-core";
import { ActionHTTPInputs } from "@al/aku-lc-schema";
import { FormattedMessage } from "@al/next/al-ex";

import linkTo from "~/common/utils/linkTo";
import Toast from "~/components/ui/Toast";

import analyzeLink from "../utils/analyzeLink";
import { FormSuccessOperateType } from "../model/form";

import { ActionHandleItem, ActionData } from "./interfaces";

export type DidMountActionPayload = {
  url: string;
  method: ActionHTTPInputs["method"];
  data?: {
    error: ActionData;
    succeeded: ActionData;
  };
  fetcher: Fetcher;
};

export type SubmitDataPayload = { data: { [key in string]: string | number } };

export type SubmitParams = { [key in string]: string | number };

// 用于表单提交
class DidMountAction extends BaseAction<DidMountActionPayload> {
  // 实现 execute 方法
  execute = async (payload?: SubmitDataPayload): Promise<void> => {
    await this.handleSubmit(payload?.data);
  };

  handleTypeDeal = (item: Omit<ActionHandleItem, "resType" | "resDataKey" | "errorCode">, data: any): number => {
    let actionCount = 0;
    switch (item.actionType) {
      case FormSuccessOperateType.Toast:
        Toast.success(item.toastText || <FormattedMessage keys="提交成功" namespaces="main" />);
        actionCount++;
        break;
      case FormSuccessOperateType.Modal: {
        if (item.modal) {
          const query = new Query(item.modal);
          // 根据visibleModalField判断，用来判断是否展示的接口字段
          const visibleModalField = this.payload?.data?.succeeded?.visibleModalField;
          const visible = visibleModalField ? data?.[visibleModalField] : true;
          query?.get()?.setBools({
            key: "_visible",
            value: visible,
          });
          actionCount++;
        }
        break;
      }
      case FormSuccessOperateType.Link:
        item.link && linkTo(analyzeLink(item.link));
        actionCount++;
        break;
      default:
        break;
    }
    return actionCount;
  };

  commonActionDeal = (handleList: ActionHandleItem[], data: any, errCode?: string): number => {
    let actionCount = 0;
    for (let index = 0; index < handleList.length; index++) {
      const item = handleList[index];
      // errorCode 是取错误码，"response" 是取返回值的某个属性。
      const compareTargetCode = item.resType === "errorCode" ? errCode : data[item.resDataKey];
      if (item.errorCode !== String(compareTargetCode)) continue;
      actionCount += this.handleTypeDeal(item, data);
    }
    return actionCount;
  };

  handleSubmit = async (params?: SubmitParams): Promise<void> => {
    // 解析地址栏，获取活动id
    const searchParams = new URLSearchParams(location.search);
    const { succeeded, error: errors, ...rest } = this.payload.data || {};

    const response = await this.payload.fetcher({
      url: this.payload.url,
      method: "POST",
      headers: {
        noCache: true,
        originData: true,
      },
      data: {
        ...rest,
        // console 对于 页面 DidMount 行为 使用  _gp_process 参数区分行为, 故在 请求里面去掉
        _gp_process: undefined,
        activityId: searchParams.get("activityId") || undefined, // 活动id
        activityRuleId: searchParams.get("activityRuleId") || undefined, // 活动规则id
        referrerUid: searchParams.get("code") || undefined, // 邀请人uid
        type: searchParams.get("channelId") || undefined, // 分享方式 分享渠道
        source: 3, // 来源 1老系统 2新系统 3中台
        ...(params || {}),
      },
    });

    const { success, errMsg, errCode, data } = response || {};
    this.updateStatus("Succeeded");
    this.result = response;
    this.executeAfterActions();
    if (success) {
      const handleList = succeeded?.handleList || [];
      const actionCount = this.commonActionDeal(handleList, data, errCode);
      // actionCount 为 0 说明自定义处理都没覆盖到，那就使用默认配置吧。
      if (actionCount === 0 && succeeded?.actionType) {
        this.handleTypeDeal(succeeded as Omit<ActionHandleItem, "resType" | "resDataKey" | "errorCode">, data);
      }
    } else {
      const handleList = errors?.handleList || [];
      const actionCount = this.commonActionDeal(handleList, data, errCode);
      // actionCount 为 0 说明自定义处理都没覆盖到，那就使用默认配置吧。
      if (actionCount === 0 && errors?.actionType) {
        this.handleTypeDeal(errors as Omit<ActionHandleItem, "resType" | "resDataKey" | "errorCode">, data);
      } else {
        if (errCode === "FE.1") return;
        // 默认弹出错误提示
        errMsg && Toast.fail(errMsg);
      }
    }
  };
}

export default DidMountAction;
